import { QRCode } from '@mentimeter/qr-code';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useTranslations } from 'next-intl';
import type { InstructionsStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { StaggerCss } from '../../shared/components/StaggerCss';
import { TitleSection } from '../../shared/components/title-section/TitleSection';

function isValidHttpUrl(string: string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

export function InstructionsPresentation() {
  const { useVotingInstructions, useConfig, InteractiveCanvasComponents } =
    usePresentationContext();

  const slide = usePresentationSlideContext();
  const staticContent = slide.staticContent as InstructionsStaticContent;
  const title = slide.title;
  const translate = useTranslations('presenting');
  const { baseUrl, votingUrl, voteKey, voteCode } = useVotingInstructions();
  const { enableAnimations } = useConfig();

  const qrUrl = `${votingUrl}/${voteKey}?source=qr-slide`;

  const isEditor = Boolean(InteractiveCanvasComponents?.Title);

  return (
    <Box width="100%" height="100%" justifyContent="center">
      {(title || isEditor) && (
        <TitleSection
          title={title}
          styledTitle={staticContent.styledTitle as Record<string, unknown>}
          fontSizeOffset={staticContent.textSizeOffset}
          textAlign="center"
          placeholder={translate('instructions_slide_title_placeholder')}
          enablePlaceholderAlignment
          mb="space4"
          showTextSizeDropdown={true}
          hideAIEditingSuggestions
        />
      )}

      {voteKey && isValidHttpUrl(qrUrl) && (
        <Box
          width="100%"
          flexDirection="row"
          height={title || isEditor ? '65%' : '100%'}
          zIndex={1}
          pt={4}
        >
          {voteCode && (
            <>
              <Box
                data-testid="vote-code-container"
                width="100%"
                height="100%"
                flex="1 1 auto"
                alignItems="center"
                justifyContent="center"
              >
                <Text color="text" fontSize="225">
                  {translate('legacy.voting_instructions.go_to')}
                </Text>
                <Text
                  fontFamily="heading"
                  fontWeight="semiBold"
                  lineHeight="heading"
                  color="text"
                  fontSize="88px"
                >
                  {baseUrl}
                </Text>
                <Text mb={2} mt={4} fontSize="225" color="text">
                  {translate('legacy.voting_instructions.enter_code')}
                </Text>
                <StaggerCss
                  type="fade"
                  height="auto"
                  index={2}
                  enableAnimations={enableAnimations}
                >
                  <Text
                    fontFamily="heading"
                    fontWeight="semiBold"
                    lineHeight="heading"
                    color="text"
                    mb={0}
                    fontSize="88px"
                    data-testid="voting-code"
                  >
                    {voteCode}
                  </Text>
                </StaggerCss>
              </Box>
              <Box height="100%" width="1px" bg="border" />
            </>
          )}
          <Box
            width="100%"
            height="100%"
            flex="1 1 auto"
            alignItems="center"
            justifyContent="center"
          >
            <Box height="100%" extend={() => ({ aspectRatio: '1' })}>
              <StaggerCss
                type="fade"
                index={2}
                enableAnimations={enableAnimations}
              >
                <Box
                  width="100%"
                  height="100%"
                  bg="white"
                  p={3}
                  borderRadius="25px"
                >
                  <QRCode url={qrUrl} />
                </Box>
              </StaggerCss>
            </Box>
            {voteCode && (
              <Text mt={3} fontSize="225" color="text">
                {translate('legacy.voting_instructions.or_qr')}
              </Text>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
